import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  X,
  ChevronDown,
  HelpCircle,
  ExternalLink,
  BarChart3,
  CalendarRange,
  Landmark,
  Wallet,
  ArrowLeftRight,
  ClipboardList,
  Settings,
  SlidersHorizontal,
  ShieldCheck,
  ShoppingCart,
  Store,
  Truck,
  BadgePercent,
  Package,
  CreditCard,
  HandCoins,
  TrendingUp,
  Factory,
  PackageCheck,
  Warehouse,
  Users,
  Tags,
  Calculator,
  Coins,
  FileText,
  TrendingDown,
  Layers,
  Percent,
  Receipt,
  ListChecks,
  PieChart,
  Scale,
  RefreshCw,
  Home,
  LayoutGrid,
  FileBarChart,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { obtenerIniciales } from '../utils/format';

// Menú del rediseño (parte 4): Inicio solo arriba; Operación por tarea
// (en restaurantes, Caja primero); Análisis con la página de Reportes;
// Administración con Contabilidad y lo de sistema. Un ítem con `hijos` es un
// grupo colapsable; si además tiene `to`, la cabecera navega a su propia
// página. `rutasExtra` marca un enlace como activo también en otras rutas
// (pantallas con pestañas). `rol`, `seccion` y `modulo` repiten lo que pide
// la ruta en App.jsx, para no mostrar lo que el usuario no puede abrir.
const JERARQUIA = ['consultor', 'operador', 'contador', 'gerente', 'admin'];

const CAJA = {
  to: '/caja', label: 'Caja', icon: Store, rol: 'operador', modulo: 'caja', end: true,
  hijos: [
    { to: '/caja/resumen', label: 'Resumen del día', icon: ClipboardList, rol: 'operador', modulo: 'caja' },
    { to: '/reportes/cierre-fiscal-diario', label: 'Cierre fiscal diario', icon: Receipt, rol: 'operador', modulo: 'caja' },
    { to: '/caja/configuracion', label: 'Configuración', icon: SlidersHorizontal, rol: 'gerente', modulo: 'caja' }
  ]
};
const BANCOS = {
  label: 'Bancos', icon: Landmark,
  hijos: [
    { to: '/bancos/resumen', label: 'Resumen', icon: LayoutGrid, seccion: 'dashboard' },
    { to: '/cuentas', label: 'Cuentas', icon: Wallet, rol: 'contador', seccion: 'cuentas' },
    { to: '/movimientos', label: 'Movimientos', icon: ArrowLeftRight, rol: 'operador', seccion: 'movimientos' },
    { to: '/conciliacion', label: 'Conciliación', icon: ListChecks, rol: 'contador', seccion: 'movimientos' },
    { to: '/reportes/flujo-caja', label: 'Flujo de caja', icon: BarChart3, seccion: 'movimientos' },
    { to: '/reportes/flujo-proyectado', label: 'Flujo proyectado', icon: CalendarRange, seccion: 'movimientos' },
    { to: '/tasas-cambio', label: 'Tasas de cambio', icon: TrendingUp, rol: 'operador', seccion: 'tasas_cambio' }
  ]
};
const VENTAS = {
  to: '/ventas', label: 'Ventas', icon: ShoppingCart, rol: 'operador', modulo: 'ventas',
  hijos: [
    { to: '/clientes', label: 'Clientes', icon: Users, rol: 'operador', modulo: 'ventas' },
    { to: '/vendedores', label: 'Vendedores', icon: Percent, rol: 'operador', modulo: 'ventas' },
    { to: '/reportes/cuentas-por-cobrar', label: 'Cuentas por cobrar', icon: HandCoins, rol: 'contador', modulo: 'reporte_cxc' }
  ]
};
const COMPRAS = {
  label: 'Compras', icon: Truck,
  hijos: [
    { to: '/proveedores', label: 'Proveedores', icon: Truck, rol: 'operador', modulo: 'proveedores', end: true },
    { to: '/reportes/cuentas-por-pagar', label: 'Cuentas por pagar', icon: CreditCard, rol: 'contador', modulo: 'reporte_cxp' },
    { to: '/proveedores/recurrentes', label: 'Facturas recurrentes', icon: RefreshCw, rol: 'operador', modulo: 'proveedores' },
    { to: '/proveedores/comparador-precios', label: 'Comparador de precios', icon: BadgePercent, modulo: 'proveedores' }
  ]
};
const INVENTARIO = {
  label: 'Inventario', icon: Package,
  hijos: [
    { to: '/productos', label: 'Productos', icon: Package, rol: 'operador', modulo: 'productos' },
    { to: '/inventario/estructura-costo', label: 'Costos y precios', icon: Layers, rol: 'operador', modulo: 'productos', rutasExtra: ['/inventario/lista-precios'] },
    { to: '/inventario/actual', label: 'Inventario actual', icon: Warehouse, rol: 'operador', modulo: 'productos' },
    { to: '/reportes/costo-actividades', label: 'Costo real de actividades', icon: Coins, rol: 'operador', modulo: 'productos' },
    { to: '/inventario/cierre', label: 'Cierre de inventario', icon: PackageCheck, rol: 'operador', modulo: 'productos' }
  ]
};
const PRODUCCION = {
  label: 'Producción', icon: Factory, modulo: 'produccion',
  hijos: [
    { to: '/produccion', label: 'Extrusión', icon: Factory, seccion: 'produccion', modulo: 'produccion_extrusion', end: true },
    { to: '/produccion/estimaciones', label: 'Estimaciones', icon: TrendingDown, seccion: 'produccion', modulo: 'produccion_extrusion' }
  ]
};
const CONTABILIDAD = {
  label: 'Contabilidad', icon: Calculator,
  hijos: [
    { to: '/reportes/estado-resultados', label: 'Estado de resultados', icon: FileText, rol: 'contador', seccion: 'movimientos' },
    { to: '/reportes/balance-general', label: 'Balance general', icon: Scale, rol: 'contador', seccion: 'movimientos' },
    { to: '/reportes/egresos-categoria', label: 'Egresos por categoría', icon: PieChart, rol: 'contador' },
    { to: '/impuestos', label: 'Impuestos', icon: Percent, rol: 'contador', modulo: 'reporte_libro_fiscal', rutasExtra: ['/reportes/libro-ventas', '/reportes/libro-compras', '/reportes/retenciones'] },
    { to: '/categorias', label: 'Cuentas contables', icon: Tags, rol: 'contador', seccion: 'movimientos' },
    { to: '/contabilidad/plan-cuentas', label: 'Plan de Cuentas', icon: BookOpen, rol: 'contador', seccion: 'movimientos' },
    { to: '/contabilidad/configuracion', label: 'Configuración contable', icon: SlidersHorizontal, rol: 'contador', seccion: 'movimientos' }
  ]
};

export function construirSecciones(rubro) {
  const operacion = rubro === 'restaurante'
    ? [CAJA, BANCOS, VENTAS, COMPRAS, INVENTARIO, PRODUCCION]
    : [BANCOS, VENTAS, COMPRAS, INVENTARIO, PRODUCCION, CAJA];
  return [
    { titulo: '', items: [{ to: '/', label: 'Inicio', icon: Home, end: true }] },
    { titulo: 'Operación', items: operacion },
    { titulo: 'Análisis', items: [{ to: '/reportes', label: 'Reportes', icon: FileBarChart, end: true }] },
    {
      titulo: 'Administración',
      items: [
        CONTABILIDAD,
        { to: '/configuracion', label: 'Configuración', icon: Settings, rol: 'gerente', seccion: 'configuracion' },
        { to: '/auditoria', label: 'Auditoría', icon: ClipboardList, rol: 'gerente', seccion: 'auditoria' }
      ]
    }
  ];
}

// Visibilidad de un ítem del menú según el rol, las secciones apagadas al
// usuario y los módulos activos de la empresa.
export function esVisible(item, rol, seccionesDeshabilitadas, modulosActivos) {
  return (!item.rol || JERARQUIA.indexOf(rol) >= JERARQUIA.indexOf(item.rol)) &&
    (!item.seccion || !seccionesDeshabilitadas.includes(item.seccion)) &&
    (!item.modulo || !!modulosActivos?.[item.modulo]);
}

// Un enlace con "?pestana=…" solo está activo con esa pestaña; el mismo
// camino sin query, solo cuando no hay pestaña.
function activoPorQuery(item, pathname, search) {
  const [ruta, query] = item.to.split('?');
  if (pathname !== ruta) return null;
  const actual = new URLSearchParams(search).get('pestana');
  return query ? actual === new URLSearchParams(query).get('pestana') : !actual;
}

function EnlaceNav({ item, colapsado, onCerrar }) {
  const Icono = item.icon;
  const { pathname, search } = useLocation();
  const activoExtra = !!item.rutasExtra?.some((r) => pathname.startsWith(r));
  const porQuery = activoPorQuery(item, pathname, search);
  const activo = (isActive) => (porQuery !== null ? porQuery : isActive || activoExtra);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onCerrar}
      title={colapsado ? item.label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-400/60
        ${
          activo(isActive)
            ? 'bg-turquoise-400/10 text-turquoise-300'
            : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
        }`
      }
    >
      {({ isActive: activoRuta }) => { const isActive = activo(activoRuta); return (
        <>
          <Icono
            size={19}
            strokeWidth={2}
            className={`flex-shrink-0 transition-colors ${isActive ? 'text-turquoise-400' : 'text-slate-500 group-hover:text-slate-200'}`}
          />
          <span className={`truncate ${colapsado ? 'md:hidden' : ''}`}>{item.label}</span>
          {isActive && (
            <span className={`ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-turquoise-400 shadow-[0_0_6px_1px_rgba(45,212,191,0.7)] ${colapsado ? 'md:hidden' : ''}`} />
          )}
        </>
      ); }}
    </NavLink>
  );
}

// Cabecera de un grupo colapsable: si `item.to` existe (tiene página
// propia) es un NavLink navegable con un chevron aparte para
// expandir/colapsar; si no, toda la fila es un botón que solo alterna.
function CabeceraGrupo({ item, expandido, onAlternar, colapsado, onCerrar, activo }) {
  const Icono = item.icon;
  const chevron = !colapsado && (
    <ChevronDown
      size={16}
      className={`ml-auto flex-shrink-0 text-slate-500 transition-transform duration-150 ${expandido ? '' : '-rotate-90'}`}
    />
  );

  const contenido = (isActive) => (
    <>
      <Icono
        size={19}
        strokeWidth={2}
        className={`flex-shrink-0 transition-colors ${isActive ? 'text-turquoise-400' : 'text-slate-500 group-hover:text-slate-200'}`}
      />
      <span className={`truncate ${colapsado ? 'md:hidden' : ''}`}>{item.label}</span>
      {chevron}
    </>
  );

  const claseBase = (isActive) =>
    `group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-400/60
    ${isActive ? 'bg-turquoise-400/10 text-turquoise-300' : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'}`;

  if (item.to) {
    return (
      <NavLink to={item.to} end={item.end} onClick={onCerrar} title={colapsado ? item.label : undefined} className={({ isActive }) => claseBase(isActive)}>
        {({ isActive }) => (
          <>
            <Icono
              size={19}
              strokeWidth={2}
              className={`flex-shrink-0 transition-colors ${isActive ? 'text-turquoise-400' : 'text-slate-500 group-hover:text-slate-200'}`}
            />
            <span className={`truncate ${colapsado ? 'md:hidden' : ''}`}>{item.label}</span>
            {!colapsado && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAlternar(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onAlternar(); } }}
                className="ml-auto flex-shrink-0 rounded-md p-0.5 text-slate-500 hover:text-slate-200"
              >
                <ChevronDown size={16} className={`transition-transform duration-150 ${expandido ? '' : '-rotate-90'}`} />
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <button type="button" onClick={onAlternar} title={colapsado ? item.label : undefined} className={claseBase(activo)}>
      {contenido(activo)}
    </button>
  );
}

function GrupoConHijos({ item, hijos, colapsado, onCerrar, expandido, onAlternar, activo }) {
  return (
    <div>
      <CabeceraGrupo item={item} expandido={expandido} onAlternar={onAlternar} colapsado={colapsado} onCerrar={onCerrar} activo={activo} />
      {!colapsado && expandido && hijos.length > 0 && (
        <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
          {hijos.map((hijo) => (
            <EnlaceNav key={hijo.to} item={hijo} colapsado={colapsado} onCerrar={onCerrar} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ abierta, onCerrar }) {
  // El sidebar SIEMPRE está minimizado en escritorio: sólo se despliega
  // (flotando sobre el contenido) mientras el mouse está encima. En móvil
  // se muestra completo vía el drawer (`abierta`).
  const [hoverExpandido, setHoverExpandido] = useState(false);
  const colapsado = !abierta && !hoverExpandido;
  // Abierto por defecto solo el grupo de la página actual; el usuario puede
  // abrir o cerrar cualquiera (se guarda como "alternado" respecto de eso).
  const [gruposAlternados, setGruposAlternados] = useState(new Set());
  const { usuario, esAdminGlobal, rolActual, seccionesDeshabilitadas, modulosActivos, empresaActivaData } = useAuth();
  const rol = esAdminGlobal ? 'admin' : rolActual || 'consultor';
  const secciones = construirSecciones(empresaActivaData?.rubro);
  const location = useLocation();
  useEffect(() => { setGruposAlternados(new Set()); }, [location.pathname]);

  function alternarGrupo(clave) {
    setGruposAlternados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(clave)) nuevo.delete(clave); else nuevo.add(clave);
      return nuevo;
    });
  }

  const visible = (item) => esVisible(item, rol, seccionesDeshabilitadas, modulosActivos);

  // Un grupo con página propia (`to`) se muestra aunque no le quede ningún
  // hijo visible; uno sin página propia solo si tiene al menos un hijo.
  // Un grupo cuya página propia está apagada (ej. Bancos → Resumen sin la
  // sección "dashboard") sigue mostrando sus hijos visibles, sin enlace.
  const seccionesVisibles = secciones
    .map((sec) => ({
      ...sec,
      items: sec.items
        .map((item) => {
          const hijos = item.hijos ? item.hijos.filter(visible) : null;
          if (visible(item)) return { ...item, hijos };
          return hijos && hijos.length > 0 && !item.modulo ? { ...item, to: null, hijos } : null;
        })
        .filter(Boolean)
        .filter((item) => !item.hijos || item.to || item.hijos.length > 0)
    }))
    .filter((sec) => sec.items.length > 0 || (sec.titulo === 'Administración' && esAdminGlobal));

  return (
    <>
      {abierta && (
        <div onClick={onCerrar} className="fixed inset-0 bg-black/40 z-30 md:hidden" />
      )}

      <aside
        onMouseEnter={() => setHoverExpandido(true)}
        onMouseLeave={() => setHoverExpandido(false)}
        onFocus={() => setHoverExpandido(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHoverExpandido(false); }}
        className={`flex flex-col bg-gradient-to-b from-navy-900 to-navy-950 text-slate-200 transition-transform duration-200 print:hidden
          fixed inset-y-0 left-0 z-40 w-[268px] ${abierta ? 'translate-x-0' : '-translate-x-full'}
          md:static md:h-screen md:translate-x-0 md:transition-[width] md:duration-200 md:w-[76px] ${
            hoverExpandido ? 'md:!w-[268px] md:shadow-2xl md:shadow-black/40' : ''
          }`}
      >
        <div className="flex items-center gap-3 px-4 pt-5 pb-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-turquoise-400 to-turquoise-600 shadow-lg shadow-turquoise-500/20">
            <BarChart3 size={22} className="text-navy-950" strokeWidth={2.5} />
          </div>
          <div className={`min-w-0 ${colapsado ? 'md:hidden' : ''}`}>
            <p className="truncate text-base font-bold text-white">Finanzas</p>
            <p className="truncate text-xs text-slate-400">Panel de control</p>
          </div>
          <button onClick={onCerrar} className="ml-auto flex-shrink-0 text-slate-400 hover:text-white md:hidden">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 pt-2">
          {seccionesVisibles.map((sec) => (
            <div key={sec.titulo || 'inicio'} className="space-y-1">
              {sec.titulo && (
                <p className={`px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${colapsado ? 'md:hidden' : ''}`}>
                  {sec.titulo}
                </p>
              )}
              {sec.items.map((item) => {
                const clave = item.to || item.label;
                const activo = (item.to && location.pathname.startsWith(item.to)) ||
                  item.hijos?.some((h) => [h.to.split('?')[0], ...(h.rutasExtra || [])].some((r) => location.pathname.startsWith(r)));
                return item.hijos && item.hijos.length > 0 ? (
                  <GrupoConHijos
                    key={clave}
                    item={item}
                    hijos={item.hijos}
                    colapsado={colapsado}
                    onCerrar={onCerrar}
                    expandido={activo !== gruposAlternados.has(clave)}
                    onAlternar={() => alternarGrupo(clave)}
                    activo={activo}
                  />
                ) : (
                  <EnlaceNav key={clave} item={item} colapsado={colapsado} onCerrar={onCerrar} />
                );
              })}
              {sec.titulo === 'Administración' && esAdminGlobal && (
                <NavLink
                  to="/administracion"
                  onClick={onCerrar}
                  title={colapsado ? 'Administración' : undefined}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors duration-150
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                    ${
                      isActive
                        ? 'border-violet-400/30 bg-violet-400/15 text-violet-300'
                        : 'border-violet-400/20 bg-violet-400/5 text-violet-300/80 hover:bg-violet-400/10'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <ShieldCheck
                        size={19}
                        strokeWidth={2}
                        className={`flex-shrink-0 ${isActive ? 'text-violet-300' : 'text-violet-400/80'}`}
                      />
                      <span className={`truncate ${colapsado ? 'md:hidden' : ''}`}>Administración</span>
                      <span className={`ml-auto flex-shrink-0 rounded-md border border-violet-400/30 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-violet-300 ${colapsado ? 'md:hidden' : ''}`}>
                        ADMIN
                      </span>
                    </>
                  )}
                </NavLink>
              )}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/5 px-3 py-3">
          {/* El manual se abre en otra pestaña para no perder la pantalla
              donde se estaba trabajando (#129). */}
          <a
            href="/ayuda"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCerrar}
            title={colapsado ? 'Ayuda' : undefined}
            className="group mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors duration-150 hover:bg-white/5 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-400/60"
          >
            <HelpCircle size={19} strokeWidth={2} className="flex-shrink-0 text-slate-500 group-hover:text-slate-200" />
            <span className={`truncate ${colapsado ? 'md:hidden' : ''}`}>Ayuda</span>
            <ExternalLink size={14} className={`ml-auto flex-shrink-0 text-slate-500 ${colapsado ? 'md:hidden' : ''}`} />
          </a>
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            {usuario?.foto_url ? (
              <img
                src={usuario.foto_url}
                alt=""
                referrerPolicy="no-referrer"
                className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-navy-800 text-xs font-semibold text-turquoise-300">
                {obtenerIniciales(usuario?.nombre)}
              </div>
            )}
            <div className={`min-w-0 flex-1 ${colapsado ? 'md:hidden' : ''}`}>
              <p className="truncate text-sm font-medium text-white">{usuario?.nombre}</p>
              <p className="truncate text-xs text-slate-400 capitalize">
                {esAdminGlobal ? 'Administrador global' : rolActual}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
